import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';

export type InputType = 'text' | 'email' | 'password' | 'search' | 'tel' | 'url';

/**
 * A labelled text input.
 *
 * The label lives inside the shadow root and is associated with the control by
 * id, which is what gives the element an accessible name. A consumer never has
 * to remember to add one, which is the usual way a form field ends up unlabelled.
 *
 * @example <v-input label="Email" type="email" hint="We never share it."></v-input>
 */
@customElement('v-input')
export class VInput extends LitElement {
  static override styles = css`
    :host {
      display: block;
      font-family: var(--v-font-family-body);
    }

    label {
      display: block;
      font-size: var(--v-type-body-small-font-size);
      line-height: var(--v-type-body-small-line-height);
      color: var(--v-colors-text-primary);
      margin-block-end: var(--v-control-gap);
    }

    input {
      inline-size: 100%;
      box-sizing: border-box;
      font-family: inherit;
      font-size: var(--v-type-body-medium-font-size);
      line-height: var(--v-type-body-medium-line-height);
      color: var(--v-colors-text-primary);
      background-color: var(--v-colors-surface-primary);
      border-style: solid;
      border-width: var(--v-control-border-width);
      border-color: var(--v-colors-borders);
      border-radius: var(--v-control-radius);
      padding: var(--v-control-padding-block) var(--v-control-padding-inline);

      &::placeholder {
        color: var(--v-colors-text-tertiary);
      }

      &:focus-visible {
        outline: var(--v-focus-ring-outset-outer-spread) solid var(--v-colors-focus-ring);
        outline-offset: var(--v-focus-ring-outset-inner-spread);
      }

      &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
      }
    }

    :host([invalid]) input {
      border-color: var(--v-colors-status-error-border);
    }

    p {
      margin-block: var(--v-control-gap) 0;
      font-size: var(--v-type-body-small-font-size);
      line-height: var(--v-type-body-small-line-height);
      color: var(--v-colors-text-secondary);
    }
  `;


  /** Visible label. An input without one has no accessible name. */
  @property() label = '';

  /** Submitted under this name when the value is read by a form. */
  @property() name = '';

  /** Current value, kept in sync as the user types. */
  @property() value = '';

  @property() placeholder = '';

  /** Help text, associated with the control through aria-describedby. */
  @property() hint = '';

  @property({ reflect: true }) type: InputType = 'text';

  @property({ type: Boolean, reflect: true }) required = false;

  @property({ type: Boolean, reflect: true }) disabled = false;

  /** Marks the control invalid, for both styling and assistive technology. */
  @property({ type: Boolean, reflect: true }) invalid = false;

  #onInput(event: Event) {
    // `input` is composed, so the native event already crosses the shadow
    // boundary and retargets to this host. Re-dispatching it would deliver two.
    this.value = (event.target as HTMLInputElement).value;
  }

  #onChange() {
    // `change` is not composed, so it stops at the shadow root. A consumer
    // listening on the host would never hear it unless it is sent again.
    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }

  override render() {
    return html`
      <label part="label" for="control">${this.label}</label>
      <input
        id="control"
        part="control"
        type=${this.type}
        name=${ifDefined(this.name || undefined)}
        .value=${this.value}
        placeholder=${ifDefined(this.placeholder || undefined)}
        aria-describedby=${ifDefined(this.hint ? 'hint' : undefined)}
        aria-invalid=${this.invalid ? 'true' : 'false'}
        ?required=${this.required}
        ?disabled=${this.disabled}
        @input=${this.#onInput}
        @change=${this.#onChange}
      />
      ${this.hint ? html`<p id="hint" part="hint">${this.hint}</p>` : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'v-input': VInput;
  }
}
